import { useEffect, useMemo, useState } from 'react';
import { CAMPAIGN_TYPE_LABELS, CAMPAIGN_TYPE_ORDER } from '../../constants/campaignTypes';
import { ArrowClockwise } from 'shared/icons';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import StatusBadge from '../StatusBadge';
import Button from '../ui/Button';
import StandardTable from '../ui/StandardTable';

/**
 * `initialStatus` = preset de quem abriu a lista (o card "Campanhas hoje" da
 * Visão geral abre em `running`). Só vale na montagem; o select continua
 * livre, e sem preset a lista abre em "Todos os Status" como sempre.
 */
const PromotionsList = ({ initialStatus = null }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsPaging, setCampaignsPaging] = useState({});
    const [filterStatus, setFilterStatus] = useState(initialStatus || 'all');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const loadCampaigns = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/promotions');

            if (response.success !== false) {
                setCampaigns(response.campaigns || []);
                setCampaignsPaging(response.paging || {});
            } else {
                throw new Error(response.error || 'Erro ao carregar campanhas');
            }
        } catch (err) {
            setError({
                title: 'Erro ao Carregar Campanhas',
                message: err.message || 'Nao foi possivel carregar as campanhas.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    const filteredCampaigns = useMemo(() => {
        let filtered = campaigns;

        // "Em andamento hoje" não é um status do ML: é o `is_running` que o
        // backend calcula com a MESMA condição do card "Campanhas hoje" (status
        // started/active, dentro do período e com participação viva). Filtrar
        // por `status === 'started'` traria também as campanhas fantasmas que o
        // sync nunca encerra — e o card diria 15 para uma lista de 27.
        if (filterStatus === 'running') {
            filtered = filtered.filter((c) => c.is_running === true);
        } else if (filterStatus !== 'all') {
            filtered = filtered.filter((c) => c.status === filterStatus);
        }

        if (filterType !== 'all') {
            filtered = filtered.filter((c) => c.type === filterType);
        }

        return filtered;
    }, [campaigns, filterStatus, filterType]);

    const formatDate = (dateString) => FormatDateTimeBr(dateString, { blank: 'N/A' });
    const getStatusBadge = (status) => <StatusBadge domain="promotion" status={status} />;
    const getTypeBadge = (type) => <StatusBadge domain="promotionType" status={type} />;

    const formatBenefits = (benefits) => {
        if (!benefits) {
            return '-';
        }
        if (benefits.type === 'REBATE') {
            return `ML: ${benefits.meli_percent || 0}% | Seller: ${benefits.seller_percent || 0}%`;
        }
        if (benefits.type === 'VOLUME') {
            return `${benefits.buy_quantity || 0}x${benefits.pay_quantity || 0} (${benefits.item_discount_percent || 0}%)`;
        }
        return JSON.stringify(benefits);
    };

    const columns = [
        {
            key: 'id',
            label: 'ID',
            getValue: (row) => row.id || '-',
            cellClassName: 'font-mono text-mono-900 dark:text-white',
        },
        {
            key: 'name',
            label: 'Nome',
            getValue: (row) => row.name || '-',
        },
        {
            key: 'type',
            label: 'Tipo',
            sortable: false,
            getValue: (row) => row.type || '-',
            render: (row) => getTypeBadge(row.type),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: false,
            getValue: (row) => row.status || '-',
            render: (row) => getStatusBadge(row.status),
        },
        {
            key: 'start_date',
            label: 'Inicio',
            getValue: (row) => formatDate(row.start_date),
        },
        {
            key: 'finish_date',
            label: 'Fim',
            getValue: (row) => formatDate(row.finish_date),
        },
        {
            key: 'deadline_date',
            label: 'Deadline',
            getValue: (row) => formatDate(row.deadline_date),
        },
        {
            key: 'benefits',
            label: 'Beneficios',
            sortable: false,
            getValue: (row) => formatBenefits(row.benefits),
        },
    ];

    if (loading) {
        return (
            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" />
                <p className="text-mono-600 dark:text-mono-400">Carregando campanhas...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
                <p className="text-red-800 dark:text-red-200 font-medium mb-2">{error.title}</p>
                <p className="text-red-600 dark:text-red-300 text-sm mb-4">{error.message}</p>
                <Button onClick={loadCampaigns} variant="secondary" size="sm">
                    Tentar Novamente
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <StandardTable
                rows={filteredCampaigns}
                columns={columns}
                rowKey={(row) => row.id}
                countLabel="campanhas"
                emptyMessage={
                    searchTerm || filterStatus !== 'all' || filterType !== 'all'
                        ? 'Nenhuma campanha encontrada com os filtros aplicados'
                        : 'Nenhuma campanha encontrada'
                }
                searchPlaceholder="Buscar por nome, ID ou tipo..."
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                toolbarRight={
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm text-mono-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-mono-700 dark:bg-mono-900 dark:text-white"
                        >
                            <option value="all">Todos os Status</option>
                            <option value="running">Em andamento hoje</option>
                            <option value="started">Ativa</option>
                            <option value="pending">Pendente</option>
                            <option value="candidate">Candidata</option>
                            <option value="finished">Finalizada</option>
                        </select>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm text-mono-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-mono-700 dark:bg-mono-900 dark:text-white"
                        >
                            <option value="all">Todos os Tipos</option>
                            {CAMPAIGN_TYPE_ORDER.map((type) => (
                                <option key={type} value={type}>
                                    {CAMPAIGN_TYPE_LABELS[type]}
                                </option>
                            ))}
                        </select>
                        <Button onClick={loadCampaigns} variant="secondary" className="flex items-center gap-2">
                            <ArrowClockwise size={18} />
                            Atualizar
                        </Button>
                    </div>
                }
                toolbarLeft={
                    campaignsPaging.total > 0 ? (
                        <span className="text-xs text-mono-500 dark:text-mono-400">
                            Fonte: {campaignsPaging.total} campanhas no backend
                        </span>
                    ) : null
                }
            />
        </div>
    );
};

export default PromotionsList;
